import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown, ArrowUpRight, Gamepad2 } from "lucide-react";
import { api, formatNum } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CreatorCard } from "@/components/CreatorCard";
import { CreationCard } from "@/components/CreationCard";
import { CreationModal } from "@/components/CreationModal";
import { GameCard } from "@/components/GameCard";
import { FeaturedGame } from "@/components/FeaturedGame";
import { Campaign } from "@/components/Campaign";
import { Orders } from "@/components/Orders";
import { RevenueCounter } from "@/components/RevenueCounter";
import { GroupDrops } from "@/components/GroupDrops";
import { DropsIntro } from "@/components/DropsIntro";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { Heart } from "lucide-react";

const DEPARTMENTS = [
  { key: "UGC", label: "UGC Creators" },
  { key: "Builder", label: "Builders" },
  { key: "Scripter", label: "Scripters" },
  { key: "GFX", label: "GFX" },
  { key: "VFX", label: "VFX" },
];

const HERO_IMG = "/etoire-logo-red.png";
const DEFAULT_SITE = {
  hero_eyebrow: "Etoire — Game Creator Studio",
  hero_image_url: "",
  hero_lines: ["We build the", "worlds players", "never want to leave."],
  hero_paragraph: "A creator collective crafting UGC, worlds and cinematics — with live player stats streamed straight from our Roblox games.",
  hero_cta_label: "See live games",
  studio_eyebrow: "The Studio",
  studio_heading: "A studio built on imagination & scale.",
  studio_body: "We are creators first. The numbers below update live from every Roblox experience we connect — real players, real reach, right now.",
  games_eyebrow: "Live on Roblox",
  games_heading: "Our games",
  games_body: "",
  drops_eyebrow: "UGC Drops · Live",
  drops_heading: "Latest drops",
  drops_body: "Fresh UGC from our communities — favourites update automatically every 30 seconds, straight from Roblox.",
  creators_eyebrow: "The Collective",
  creators_heading: "Meet the creators",
  work_eyebrow: "Selected Work",
  work_heading: "Creations & drops",
  work_body: "Worlds, UGC items, systems and cinematics — a slice of what the collective ships.",
  marquee_items: ["WORLD BUILDING", "UGC DESIGN", "CINEMATICS", "SYSTEMS", "TRAILERS", "LIVE OPS"],
  manifesto: [
    { n: "01", title: "The Vision", body: "Etoire is a collective of creators obsessed with craft. We design games, UGC and cinematics that don't just get played — they get lived in." },
    { n: "02", title: "The Craft", body: "Every map, item and trailer is engineered to the last pixel. We treat player attention as the rarest currency and spend it deliberately." },
    { n: "03", title: "The Reach", body: "Millions of live sessions across our connected Roblox experiences — tracked here in real time, straight from the source." },
  ],
};

function HeroLine({ line, i }) {
  return (
    <span className="reveal-mask">
      <motion.span className="block"
        initial={{ y: "110%" }} animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}>
        {line}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const [team, setTeam] = useState([]);
  const [creations, setCreations] = useState([]);
  const [games, setGames] = useState([]);
  const [site, setSite] = useState(DEFAULT_SITE);
  const [groups, setGroups] = useState([]);
  const [drops, setDrops] = useState([]);
  const [ugc, setUgc] = useState({ items: [], clothing: [], total_favorites: 0, total_items: 0 });
  const [active, setActive] = useState(null);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/creations").then((r) => setCreations(r.data)).catch(() => {});
    api.get("/drops?active_only=true").then((r) => setDrops(r.data)).catch(() => {});
    api.get("/site").then((r) => setSite({ ...DEFAULT_SITE, ...r.data })).catch(() => {});
    api.get("/games").then((r) => setGames(r.data)).catch(() => {});
    const refresh = () => api.get("/games/refresh").then((r) => setGames(r.data)).catch(() => {});
    refresh();
    const id = setInterval(refresh, 45000);

    const refreshUgc = () => {
      api.get("/ugc").then((r) => setUgc(r.data)).catch(() => {});
      api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
    };
    refreshUgc();
    const ugcId = setInterval(refreshUgc, 30000);
    return () => { clearInterval(id); clearInterval(ugcId); };
  }, []);

  const totals = useMemo(() => {
    const sum = (k) => games.reduce((a, g) => a + (Number(g[k]) || 0), 0);
    return {
      playing: sum("playing"),
      visits: sum("visits"),
      favorites: sum("favorites"),
      count: games.length,
    };
  }, [games]);

  const featured = useMemo(
    () => games.find((g) => g.featured) || [...games].sort((a, b) => (b.playing || 0) - (a.playing || 0))[0] || null,
    [games]
  );

  const itemsByGroup = useMemo(() => {
    const m = {};
    for (const it of ugc.items) { (m[it.group_uuid] = m[it.group_uuid] || []).push(it); }
    return m;
  }, [ugc.items]);

  const clothingByGroup = useMemo(() => {
    const m = {};
    for (const it of (ugc.clothing || [])) { (m[it.group_uuid] = m[it.group_uuid] || []).push(it); }
    return m;
  }, [ugc.clothing]);

  const creatorGroups = useMemo(() => {
    const out = [];
    for (const d of DEPARTMENTS) {
      const inDept = team.filter((c) => (c.department || "UGC") === d.key);
      const leads = inDept.filter((c) => (c.tier || "Member") === "Lead");
      const members = inDept.filter((c) => (c.tier || "Member") !== "Lead");
      if (leads.length) out.push({ key: `${d.key}-lead`, heading: `Lead ${d.label}`, creators: leads });
      if (members.length) out.push({ key: `${d.key}-member`, heading: d.label, creators: members });
    }
    return out;
  }, [team]);

  const stats = [
    [formatNum(totals.playing), "Playing Now", true],
    [formatNum(totals.visits), "Total Visits", false],
    [formatNum(totals.favorites), "Favorites", false],
    [String(totals.count), "Live Games", false],
  ];

  return (
    <div className="relative">
      <DropsIntro drops={drops} team={team} />
      <Header />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(255,46,46,0.22),transparent_62%)]" />
          <img src="/etoire-logo-red.png" alt="" className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 w-[min(92vw,900px)] max-h-[72vh] object-contain opacity-90 drop-shadow-[0_0_70px_rgba(255,46,46,0.4)]" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/55 to-background/10" />
          <div className="absolute inset-0 grid-lines opacity-50" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative max-w-[1600px] w-full mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-32">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 bg-volt animate-pulse" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">{site.hero_eyebrow}</span>
          </motion.div>

          <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-8xl tracking-tighter uppercase leading-[0.95]">
            {(site.hero_lines || []).map((l, i) => <HeroLine key={i} line={l} i={i} />)}
          </h1>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-16">
            <p className="max-w-md text-muted-foreground leading-relaxed">
              {site.hero_paragraph}
            </p>
            <a href="#games" data-testid="hero-cta" className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase bg-volt text-white px-6 py-4 hover:brightness-110 transition-all duration-200 w-fit">
              {site.hero_cta_label} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
            </a>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="absolute bottom-6 right-6 md:right-10 flex items-center gap-2 text-muted-foreground">
          <ArrowDown className="w-4 h-4 animate-bounce" />
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase">Scroll</span>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 border-y border-border bg-background overflow-hidden">
        <Marquee speed={40} gradient={false}>
          {[...(site.marquee_items || []), ...(site.marquee_items || [])].map((t, i) => (
            <span key={i} className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase mx-8 text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.25)]">
              {t} <span className="text-volt [-webkit-text-stroke:0]">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* STUDIO + LIVE STATS */}
      <section id="studio" className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">{site.studio_eyebrow}</span>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight uppercase mt-6 leading-[1.05]">
              {site.studio_heading}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-muted-foreground leading-relaxed">
              {site.studio_body}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-14 mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-volt animate-pulse" />
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Live · auto-refreshing</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
          {stats.map(([v, l, live], i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }} className="bg-card p-8 md:p-10" data-testid={`stat-${l.toLowerCase().replace(/ /g, '-')}`}>
              <div className={`font-display font-black text-3xl md:text-5xl tracking-tighter ${live ? "text-volt text-glow" : "text-glow"}`}>{v}</div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-2">{l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED SPOTLIGHT */}
      {featured && <FeaturedGame game={featured} />}

      {/* GAMES */}
      <section id="games" className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">{site.games_eyebrow}</span>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight uppercase mt-4">{site.games_heading}</h2>
          </div>
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">{games.length} connected</span>
        </div>
        {games.length === 0 ? (
          <div className="border border-dashed border-border p-16 flex flex-col items-center text-center gap-4">
            <Gamepad2 className="w-8 h-8 text-muted-foreground" />
            <p className="text-muted-foreground max-w-sm">No games connected yet. Connect a Roblox game in the admin console to start tracking live stats here.</p>
            <Link to="/login" className="font-mono text-xs tracking-[0.2em] uppercase text-volt hover:brightness-125 transition-all duration-200">Go to console →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((g, i) => <GameCard key={g.id} game={g} index={i} />)}
          </div>
        )}
      </section>

      {/* UGC DROPS — LIVE */}
      <section id="drops" className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div>
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-volt animate-pulse" />
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">{site.drops_eyebrow}</span>
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight uppercase mt-4">{site.drops_heading}</h2>
            <p className="text-muted-foreground max-w-md mt-3 text-sm">{site.drops_body}</p>
          </div>
          <div className="flex items-center gap-4 border border-border bg-card px-6 py-5" data-testid="total-favorites">
            <Heart className="w-8 h-8 text-volt shrink-0" fill="currentColor" />
            <div>
              <AnimatedNumber value={ugc.total_favorites} className="font-display font-black text-4xl md:text-5xl tracking-tighter text-glow leading-none" testid="total-favorites-value" />
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1.5">Total Favorites · {ugc.total_items} items</div>
            </div>
          </div>
        </div>

        {groups.filter((g) => (itemsByGroup[g.id] || []).length > 0 || (clothingByGroup[g.id] || []).length > 0).length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="text-muted-foreground">No UGC yet. Add the Roblox communities that drop UGC from the admin console — their items and favourites will stream in here live.</p>
          </div>
        ) : (
          <div>
            {groups.filter((g) => (itemsByGroup[g.id] || []).length > 0 || (clothingByGroup[g.id] || []).length > 0).map((g) => (
              <GroupDrops key={g.id} group={g} items={itemsByGroup[g.id] || []} clothing={clothingByGroup[g.id] || []} />
            ))}
          </div>
        )}
      </section>


      {/* CREATORS */}
      <section id="creators" className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">{site.creators_eyebrow}</span>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight uppercase mt-4">{site.creators_heading}</h2>
          </div>
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">{team.length} members</span>
        </div>
        {team.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="text-muted-foreground">No creators added yet. Add your team from the admin console.</p>
          </div>
        ) : (
          <div className="space-y-16">
            {creatorGroups.map((grp) => (
              <div key={grp.key} data-testid={`creator-group-${grp.key}`}>
                <div className="flex items-center gap-4 mb-8">
                  <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight uppercase">{grp.heading}</h3>
                  <div className="flex-1 h-px bg-border" />
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{grp.creators.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {grp.creators.map((c, i) => <CreatorCard key={c.id} creator={c} index={i} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* WORK / BENTO */}
      <section id="work" className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">{site.work_eyebrow}</span>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight uppercase mt-4">{site.work_heading}</h2>
          </div>
          <p className="max-w-xs text-sm text-muted-foreground">{site.work_body}</p>
        </div>
        {creations.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="text-muted-foreground">No work added yet. Upload images &amp; videos of your work from the admin console — they'll appear here for viewers to open and watch.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] gap-6">
            {creations.map((c, i) => <CreationCard key={c.id} creation={c} index={i} onOpen={setActive} />)}
          </div>
        )}
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">Manifesto</span>
        <div className="mt-16 space-y-px">
          {(site.manifesto || []).map((m) => (
            <motion.div key={m.n} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="grid md:grid-cols-12 gap-6 py-12 border-t border-border group hover:bg-card transition-colors duration-300">
              <div className="md:col-span-2 font-mono text-sm tracking-[0.2em] text-volt">{m.n} /</div>
              <h3 className="md:col-span-4 font-display font-bold text-2xl md:text-3xl tracking-tight uppercase group-hover:translate-x-2 transition-transform duration-300">{m.title}</h3>
              <p className="md:col-span-6 text-muted-foreground leading-relaxed text-lg">{m.body}</p>
            </motion.div>
          ))}
          <div className="border-t border-border" />
        </div>
      </section>

      <Orders />

      <RevenueCounter />

      <Campaign site={site} />

      <Footer site={site} />
      <CreationModal creation={active} onClose={() => setActive(null)} />
    </div>
  );
}
